import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db/db';
import { ChevronLeft, Calendar, MapPin, Video, Edit2, Trash2 } from 'lucide-react';
import { SectionHeader, Button } from '@/components/ui';
import { EliteCard } from '@/components/ui/EliteCard';
import { format, parseISO } from 'date-fns';
import { AnalysisTimeline } from '@/components/ui/AnalysisTimeline';

export function MatchDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [showAnalysis, setShowAnalysis] = useState(false);
    const [analysisDone, setAnalysisDone] = useState(false);

    const data = useLiveQuery(async () => {
        if (!id) return null;
        const matchId = Number(id);
        const match = await db.matches.get(matchId);
        const stats = await db.matchStats.where('matchId').equals(matchId).first();
        return { match, stats };
    }, [id]);

    if (!data?.match) return null;
    const { match, stats } = data;

    const handleDelete = async () => {
        if (confirm('Are you sure you want to delete this match record?')) {
            await db.matches.delete(Number(id));
            if (stats && stats.id) {
                await db.matchStats.delete(stats.id);
            }
            navigate('/matches');
        }
    };

    const isWin = match.result === 'Win';

    return (
        <div className="min-h-screen bg-bg pb-24 animate-fade-in">
            {/* Header */}
            <div className="bg-bg-surface border-b border-white/5 pt-12 pb-4 px-4 sticky top-0 z-10 backdrop-blur-md bg-bg-surface/80">
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => navigate('/matches')}
                        className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-text-muted hover:text-white transition-colors"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <div className="flex gap-2">
                        <button
                            onClick={() => navigate(`/matches/${id}/edit`)}
                            className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-text-muted hover:text-gold-base transition-colors"
                        >
                            <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                            onClick={handleDelete}
                            className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-text-muted hover:text-status-error transition-colors"
                        >
                            <Trash2 className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            <div className="px-4 py-6 space-y-8 max-w-lg mx-auto">
                {/* 1. Scoreboard Card */}
                <div className="relative overflow-hidden rounded-3xl bg-bg-elevated border border-white/10 p-6 text-center">
                    {/* Background Glow */}
                    <div className={`absolute inset-0 opacity-20 pointer-events-none ${isWin ? 'bg-gradient-to-br from-status-success to-transparent' : 'bg-gradient-to-br from-status-error to-transparent'}`} />

                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 border ${isWin ? 'bg-status-success/10 text-status-success border-status-success/20' : 'bg-status-error/10 text-status-error border-status-error/20'}`}>
                        {match.result} via {match.method}
                    </span>

                    <div className="flex items-center justify-center gap-8 mb-2">
                        <div className="text-center">
                            <span className="block text-xs uppercase text-text-muted font-bold tracking-wider mb-2">You</span>
                            <span className="text-5xl font-black text-white leading-none">{match.myScore}</span>
                        </div>
                        <span className="text-2xl font-bold text-white/20">-</span>
                        <div className="text-center">
                            <span className="block text-xs uppercase text-text-muted font-bold tracking-wider mb-2">Opp</span>
                            <span className="text-5xl font-black text-white/60 leading-none">{match.opponentScore}</span>
                        </div>
                    </div>

                    <div className="mt-6 flex flex-col items-center gap-2">
                        <h2 className="text-xl font-bold text-white max-w-[200px] leading-tight mx-auto">
                            vs. {match.opponentName}
                        </h2>
                        <div className="flex items-center gap-3 text-xs text-text-muted font-medium">
                            <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {format(parseISO(match.date), 'MMMM d, yyyy')}
                            </span>
                            {match.eventName && (
                                <>
                                    <span>•</span>
                                    <span className="flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {match.eventName}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* 2. Detailed Stats */}
                {stats && (
                    <div className="space-y-3">
                        <SectionHeader title="Match Stats" />
                        <div className="grid grid-cols-3 gap-3">
                            <EliteCard className="p-3 text-center">
                                <span className="block text-[10px] text-text-muted font-bold tracking-widest uppercase mb-1">Takedowns</span>
                                <span className="text-xl font-black text-white">{stats.takedowns}</span>
                                <span className="block text-[9px] text-text-muted mt-0.5">{stats.takedown_attempts} attempts</span>
                            </EliteCard>
                            <EliteCard className="p-3 text-center">
                                <span className="block text-[10px] text-text-muted font-bold tracking-widest uppercase mb-1">Escapes</span>
                                <span className="text-xl font-black text-white">{stats.escapes}</span>
                            </EliteCard>
                            <EliteCard className="p-3 text-center">
                                <span className="block text-[10px] text-text-muted font-bold tracking-widest uppercase mb-1">Reversals</span>
                                <span className="text-xl font-black text-white">{stats.reversals}</span>
                            </EliteCard>
                            <EliteCard className="p-3 text-center col-span-2">
                                <span className="block text-[10px] text-text-muted font-bold tracking-widest uppercase mb-1">Near Fall Pts</span>
                                <span className="text-xl font-black text-gold-500">{stats.near_fall_points}</span>
                            </EliteCard>
                            <EliteCard className="p-3 text-center bg-red-500/5 border-red-500/20">
                                <span className="block text-[10px] text-red-400 font-bold tracking-widest uppercase mb-1">Penalty</span>
                                <span className="text-xl font-black text-red-400">{stats.penalties}</span>
                            </EliteCard>
                        </div>
                    </div>
                )}

                {/* 3. Notes Section */}
                {match.notes && (
                    <div className="space-y-3">
                        <SectionHeader title="Match Notes" />
                        <div className="bg-bg-elevated border border-white/5 rounded-2xl p-4 text-text-secondary leading-relaxed text-sm">
                            {match.notes}
                        </div>
                    </div>
                )}

                {/* 4. Video Analysis Placeholder */}
                <div className="space-y-3 pb-6">
                    <SectionHeader title="Video Analysis" />
                    {match.videoEntryId ? (
                        <div className="bg-bg-elevated border border-white/5 rounded-2xl p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-gold-base/10 flex items-center justify-center text-gold-base">
                                    <Video className="w-5 h-5" />
                                </div>
                                <div>
                                    <span className="block text-sm font-bold text-white">Match Video Linked</span>
                                    <span className="text-xs text-text-muted">Tap to view analysis</span>
                                </div>
                            </div>
                            {analysisDone ? (
                                <Button variant="primary" size="sm" onClick={() => alert('View Report (Coming Soon)')}>
                                    View Report
                                </Button>
                            ) : (
                                <Button variant="secondary" size="sm" onClick={() => setShowAnalysis(true)}>
                                    Analyze
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="bg-bg-elevated border border-white/5 border-dashed rounded-2xl p-6 text-center">
                            <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-3 text-text-muted">
                                <Video className="w-6 h-6" />
                            </div>
                            <p className="text-sm text-text-secondary mb-4">
                                No video linked to this match yet.
                            </p>
                            <Button variant="secondary" size="sm" onClick={() => { }}>
                                Link Video
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <AnalysisTimeline
                isVisible={showAnalysis}
                onComplete={() => {
                    setShowAnalysis(false);
                    setAnalysisDone(true);
                }}
                title="Match Intelligence"
                steps={[
                    { id: '1', label: 'EXTRACTING VIDEO FRAMES', durationMs: 1500 },
                    { id: '2', label: 'EVALUATING MOTION VECTORS', durationMs: 2000 },
                    { id: '3', label: 'DETECTING SCORING ACTIONS', durationMs: 2500 },
                    { id: '4', label: 'GENERATING TIMESTAMP LOG', durationMs: 1500 },
                ]}
            />
        </div>
    );
}
